import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, ArrowRight, Check, Circle, FileText, Loader2 } from 'lucide-react'
import { useAnalytics } from '@/hooks/use-analytics'
import { useEffect, useRef, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  getTamReportsAppTamReportsGetOptions,
} from '@/client/@tanstack/react-query.gen'
import { loadTamData } from './-components/load-tam-data'
import { TamCompaniesTable } from './-components/tam-companies-table'
import { TamExecutiveSummary } from './-components/tam-executive-summary'
import { TamMethodologyButton } from './-components/tam-filters-applied'
import { TamGeographicMap } from './-components/tam-geographic-map'
import { TamGrowthEvents } from './-components/tam-growth-events'
import { TamHeroStats } from './-components/tam-hero-stats'
import { TamIndustryChart } from './-components/tam-industry-chart'
import { TamInsight } from './-components/tam-insight'
import { TamIntentSignals } from './-components/tam-intent-signals'
import { TamKeywordBreakdown } from './-components/tam-keyword-breakdown'
import { TamRevenueByIndustry } from './-components/tam-revenue-by-industry'
import { TamRevenueChart } from './-components/tam-revenue-chart'
import { TamSizeByIndustry } from './-components/tam-size-by-industry'
import { TamReportSkeleton } from './-components/tam-report-skeleton'
import { TamSizeChart } from './-components/tam-size-chart'

function isNonEmptyRecord(record: Record<string, number>): boolean {
  return Object.keys(record).length > 0
}

/** Aggregate location counts across all NAICS categories into a single state->count map */
function aggregateStateCounts(
  categoriesByLocation: Record<string, Record<string, number>> | undefined,
): Record<string, number> {
  if (!categoriesByLocation)
    return {}

  const counts: Record<string, number> = {}
  for (const locations of Object.values(categoriesByLocation)) {
    for (const [location, count] of Object.entries(locations)) {
      if (location === 'total' || typeof count !== 'number')
        continue
      counts[location] = (counts[location] ?? 0) + count
    }
  }
  return counts
}

function flattenCategoryStats(
  statsByCategory: Record<string, Record<string, number>> | undefined,
): Record<string, number> {
  if (!statsByCategory) {
    return {}
  }

  const flattened: Record<string, number> = {}
  for (const [category, values] of Object.entries(statsByCategory)) {
    // eslint-disable-next-line dot-notation -- TS4111: index signature requires bracket access
    if (typeof values['total'] === 'number') {
      // eslint-disable-next-line dot-notation -- TS4111: index signature requires bracket access
      flattened[category] = values['total']
      continue
    }

    let sum = 0
    for (const [bucket, count] of Object.entries(values)) {
      if (bucket !== 'total' && typeof count === 'number') {
        sum += count
      }
    }
    if (sum > 0) {
      flattened[category] = sum
    }
  }
  return flattened
}

/** Aggregate bucket counts (e.g. employee ranges) across all NAICS categories */
function flattenBucketStats(
  statsByCategory: Record<string, Record<string, number>> | undefined,
): Record<string, number> {
  if (!statsByCategory)
    return {}

  const flattened: Record<string, number> = {}
  for (const values of Object.values(statsByCategory)) {
    for (const [bucket, count] of Object.entries(values)) {
      if (bucket === 'total' || typeof count !== 'number')
        continue
      flattened[bucket] = (flattened[bucket] ?? 0) + count
    }
  }
  return flattened
}

export const Route = createFileRoute('/_authed/_user/tam-report/')({
  component: TamReportPage,
  validateSearch: (search: Record<string, unknown>) => {
    // eslint-disable-next-line dot-notation -- TS4111: index signature requires bracket access
    const raw = search['reportId']
    let reportId: number | undefined
    if (typeof raw === 'string') {
      reportId = Number(raw)
    }
    else if (typeof raw === 'number') {
      reportId = raw
    }
    // eslint-disable-next-line dot-notation -- TS4111: index signature requires bracket access
    const from = search['from'] as string | undefined
    return { reportId, ...(from ? { from } : {}) }
  },
  beforeLoad: () => ({
    getTitle: () => 'Market Intelligence Report',
  }),
})

function isAnyGenerating(reports: Array<{ processingStatus?: string | null | undefined }>) {
  return reports.some(r => r.processingStatus === 'PENDING' || !r.processingStatus)
}

function TamReportPage() {
  const { reportId: searchReportId, from } = Route.useSearch()
  const isFromOnboarding = from === 'onboarding'
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(isFromOnboarding)
  const { capture } = useAnalytics()
  const tamViewedRef = useRef(false)

  const { data: reports, isLoading: reportsLoading } = useQuery({
    ...getTamReportsAppTamReportsGetOptions(),
    refetchInterval: (query) => {
      const data = query.state.data
      return data && isAnyGenerating(data) ? 5000 : undefined
    },
  })

  // Auto-select: prefer completed report, fall back to any report (e.g. generating)
  const completedReports = reports?.filter(r => r.processingStatus === 'COMPLETED') ?? []
  const selectedReportId = searchReportId ?? completedReports[0]?.id ?? reports?.[0]?.id
  const selectedReport = reports?.find(r => r.id === selectedReportId)

  useEffect(() => {
    if (reportsLoading || tamViewedRef.current || reports === undefined)
      return
    tamViewedRef.current = true
    let entry: 'default' | 'deep_link' | 'onboarding' = 'default'
    if (isFromOnboarding) {
      entry = 'onboarding'
    }
    else if (searchReportId !== undefined) {
      entry = 'deep_link'
    }
    capture('tam_report_viewed', {
      report_count: reports.length,
      entry,
      selected_report_processing_status: selectedReport?.processingStatus ?? 'none',
    })
  }, [
    capture,
    reportsLoading,
    reports,
    isFromOnboarding,
    searchReportId,
    selectedReport?.processingStatus,
  ])

  // Fetch report data
  const { data, isLoading: dataLoading } = useQuery({
    queryKey: ['tam-report-data', selectedReportId],
    queryFn: () => loadTamData(selectedReportId!),
    enabled: !!selectedReportId && selectedReport?.processingStatus === 'COMPLETED',
    refetchInterval: reports && isAnyGenerating(reports) ? 5000 : undefined,
  })

  if (reportsLoading) {
    return <TamReportSkeleton />
  }

  if (!reports || reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <FileText className="h-12 w-12 text-muted-foreground/40" />
        <div className="text-center">
          <h2 className="text-lg font-semibold">No Market Intelligence Report</h2>
          <p className="text-sm text-muted-foreground">
            Reports can be generated from the admin portal.
          </p>
        </div>
      </div>
    )
  }

  // Show status for non-completed reports
  if (selectedReport && selectedReport.processingStatus !== 'COMPLETED') {
    const isFailed = selectedReport.processingStatus === 'FAILED'
    return (
      <div className="flex flex-col gap-8">
        {isFailed
          ? (
              <div className="flex flex-col items-center justify-center gap-4 py-20">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <div className="text-center">
                  <h2 className="text-lg font-semibold">Report Generation Failed</h2>
                  <p className="text-sm text-muted-foreground">
                    This report could not be generated. Try generating a new one from the admin portal.
                  </p>
                </div>
              </div>
            )
          : (
              <>
                {showOnboardingBanner && (
                  <Alert className="border-primary/20 bg-primary/5">
                    <AlertDescription className="flex items-center justify-between">
                      <span>
                        {'Your Market Intelligence Report is being generated. You can always find it under '}
                        <strong>Market Intelligence</strong>
                        {' in the sidebar.'}
                      </span>
                      <a
                        href="/"
                        className="ml-4 inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline"
                        onClick={() => setShowOnboardingBanner(false)}
                      >
                        Go to dashboard
                        <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    </AlertDescription>
                  </Alert>
                )}
                <GenerationProgress report={selectedReport} />
              </>
            )}
      </div>
    )
  }

  if (dataLoading || !data) {
    return <TamReportSkeleton />
  }

  const generatedAt = new Date(data.dashboardSummary.generated_at).toLocaleString()

  const fallbackIndustryBreakdown = flattenCategoryStats(data.statsKeywords.stats.business_categories_per_location)
  const fallbackCompanySizeBreakdown = flattenBucketStats(data.statsKeywords.stats.number_of_employees_per_category)
  const fallbackRevenueBreakdown = flattenBucketStats(data.statsKeywords.stats.revenue_per_category)

  const industryBreakdown = isNonEmptyRecord(data.dashboardSummary.industry_breakdown)
    ? data.dashboardSummary.industry_breakdown
    : fallbackIndustryBreakdown
  const companySizeBreakdown = isNonEmptyRecord(data.dashboardSummary.company_size_breakdown)
    ? data.dashboardSummary.company_size_breakdown
    : fallbackCompanySizeBreakdown
  const revenueBreakdown = isNonEmptyRecord(data.dashboardSummary.revenue_breakdown)
    ? data.dashboardSummary.revenue_breakdown
    : fallbackRevenueBreakdown

  const analysis = data.analysis

  return (
    <div className="flex flex-col gap-8">
      {/* Header (excluded from PDF) */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {data.dashboardSummary.customer_name}
          </h1>
          <p className="text-sm text-muted-foreground">
            Market Intelligence Report
            <span className="mx-1.5 text-muted-foreground/40">|</span>
            {generatedAt}
          </p>
        </div>
        <TamMethodologyButton filters={data.filtersApplied} />
      </div>

      <div className="flex flex-col gap-8">

        {/* Section 1: Market Overview */}
        <section className="grid gap-4 lg:grid-cols-2 items-stretch">
          <TamExecutiveSummary text={analysis?.executive_summary} />
          <TamHeroStats summary={data.dashboardSummary} />
        </section>

        {/* Section 2: Market Composition */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Market Composition</h2>
            <p className="text-sm text-muted-foreground">Industry, geography, revenue, and size breakdown of your addressable market</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TamGeographicMap
              stateCounts={aggregateStateCounts(data.statsKeywords.stats.business_categories_per_location)}
              cityRegionData={data.keywordCityRegions?.data}
              insight={analysis?.sections.geographic}
            />
            <TamIndustryChart
              industryBreakdown={industryBreakdown}
              insight={analysis?.sections.industry}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TamRevenueByIndustry
              revenuePerCategory={data.statsKeywords.stats.revenue_per_category ?? {}}
              naicsLabels={data.filtersApplied.naics_labels}
              insight={analysis?.sections.revenue_by_industry}
            />
            <TamSizeByIndustry
              employeesPerCategory={data.statsKeywords.stats.number_of_employees_per_category ?? {}}
              naicsLabels={data.filtersApplied.naics_labels}
              insight={analysis?.sections.size_by_industry}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TamRevenueChart
              revenueBreakdown={revenueBreakdown}
              insight={analysis?.sections.revenue_distribution}
            />
            <TamSizeChart
              companySizeBreakdown={companySizeBreakdown}
              insight={analysis?.sections.size_distribution}
            />
          </div>
        </section>

        {/* Section 3: Active Buying Signals */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Active Buying Signals</h2>
            <p className="text-sm text-muted-foreground">Time-sensitive intent, keyword, and growth signals from businesses in your market</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <TamKeywordBreakdown
              keywordBreakdown={data.dashboardSummary.keyword_breakdown}
              totalKeywordMatchedBusinesses={data.dashboardSummary.headline_stats.keyword_matched_businesses}
              insight={analysis?.sections.keywords}
            />
            <TamIntentSignals
              intentBusinesses={data.dashboardSummary.headline_stats.intent_signaled_businesses}
              totalBusinesses={data.dashboardSummary.headline_stats.total_businesses}
              insight={analysis?.sections.intent_signals}
            />
            <TamGrowthEvents
              growthEventBusinesses={data.dashboardSummary.headline_stats.growth_event_businesses}
              totalBusinesses={data.dashboardSummary.headline_stats.total_businesses}
              insight={analysis?.sections.growth_events}
            />
          </div>
        </section>

        {/* Section 4: Sample Companies */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Sample Companies</h2>
              <p className="text-sm text-muted-foreground">Representative businesses matching your keyword and industry criteria</p>
            </div>
            <span className="text-xs text-muted-foreground">
              {data.companiesMerged.total_results.toLocaleString()}
              {' '}
              results
            </span>
          </div>
          <TamInsight text={analysis?.sections.companies_intro} />
          <TamCompaniesTable companies={data.companiesMerged.data} />
        </section>
      </div>
    </div>
  )
}

const GENERATION_STEPS = [
  { key: 'config', label: 'Researching company', description: 'AI is analyzing the company description and generating search parameters' },
  { key: 'data', label: 'Pulling market data', description: 'Fetching business statistics, companies, and geographic data from Explorium' },
  { key: 'analysis', label: 'Generating analysis', description: 'AI is writing executive summary and section insights' },
] as const

function getActiveStep(report: { hasConfig?: boolean, hasTamData?: boolean }): number {
  if (!report.hasConfig)
    return 0
  if (!report.hasTamData)
    return 1
  return 2
}

function GenerationProgress({ report }: { report: { displayName: string, hasConfig?: boolean, hasTamData?: boolean } }) {
  const activeStep = getActiveStep(report)

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-16">
      <div className="text-center space-y-2">
        <h2 className="text-lg font-semibold">
          Generating:
          {' '}
          {report.displayName}
        </h2>
        <p className="text-sm text-muted-foreground">
          This may take several minutes. The page will update automatically.
        </p>
      </div>

      <div className="w-full max-w-md space-y-3">
        {GENERATION_STEPS.map((step, index) => {
          const isComplete = index < activeStep
          const isActive = index === activeStep

          return (
            <div key={step.key} className="flex items-start gap-3">
              <div className="mt-0.5 shrink-0">
                {isComplete && <Check className="h-5 w-5 text-green-600" />}
                {isActive && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                {!isComplete && !isActive && <Circle className="h-5 w-5 text-muted-foreground/30" />}
              </div>
              <div>
                <p className={`text-sm font-medium ${isComplete ? 'text-green-600' : ''} ${isActive ? 'text-foreground' : ''} ${!isComplete && !isActive ? 'text-muted-foreground/50' : ''}`}>
                  {step.label}
                </p>
                {isActive && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
